import { ReactNode } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { formatDayTitle } from "../DailyExpense/format";
import {
  ACTUAL_BADGE_TEXT,
  ESTIMATE_BADGE_TEXT,
  NOT_APPROVED_BADGE_TEXT,
} from "./constants";
import { ReportLine, Thresholds, Tone, tonesOf } from "./incomeExpenseReport";
import {
  countText,
  isNegativeMoney,
  isNegativePercent,
  moneyText,
  percentText,
} from "./reportFormat";

const DRAWER_WIDTH = 420;
const PERCENT_DECIMALS = 2;
const WHOLE_PERCENT_DECIMALS = 0;
const TONE_COLOR: Record<Tone, string> = {
  good: "success.dark",
  warn: "warning.dark",
  bad: "error.main",
};

/** ฝ่ายขาย / บริหาร: the estimate, or (actual) a day with no approved daily document yet. */
const expenseBadgeOf = (isEstimate: boolean, isMissing: boolean): string | undefined => {
  if (isEstimate) return ESTIMATE_BADGE_TEXT;
  return isMissing ? NOT_APPROVED_BADGE_TEXT : undefined;
};

interface DetailRowProps {
  label: string;
  value: string;
  isNegative?: boolean;
  /** ประมาณการ: italic, with the badge. */
  isEstimate?: boolean;
  badge?: string;
}

function DetailRow({ label, value, isNegative = false, isEstimate = false, badge }: Readonly<DetailRowProps>) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {badge && <Chip size="small" variant="outlined" label={badge} />}
      </Stack>
      <Typography
        variant="body2"
        sx={{
          fontVariantNumeric: "tabular-nums",
          ...(isEstimate && { fontStyle: "italic", color: "primary.dark" }),
          ...(isNegative && { color: "error.main" }),
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function Section({ title, total, children }: Readonly<{ title: string; total: string; children: ReactNode }>) {
  return (
    <Stack gap={0.75}>
      <Stack direction="row" justifyContent="space-between">
        <Typography fontWeight={600}>{title}</Typography>
        <Typography fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {total}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
}

interface RouteDayDrawerProps {
  /** The day open in the drawer; null = closed. */
  line: ReportLine | null;
  thresholds: Thresholds | null;
  isEstimate: boolean;
  showsMissingDocument: boolean;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
}

/** One route-day in detail: sales, cost, the two expense groups and the fuel inside Sales. */
export default function RouteDayDrawer({
  line,
  thresholds,
  isEstimate,
  showsMissingDocument,
  onPrevious,
  onNext,
  onClose,
}: Readonly<RouteDayDrawerProps>) {
  const figures = line?.figures;
  const profitTone = figures ? tonesOf(figures, thresholds).profit : null;
  const isMissing = showsMissingDocument && (figures?.missing_expense_count ?? 0) > 0;
  const expenseBadge = expenseBadgeOf(isEstimate, isMissing);

  return (
    <Drawer anchor="right" open={line !== null} onClose={onClose}>
      {line && figures && (
        <Stack gap={2} sx={{ width: DRAWER_WIDTH, p: 3, height: "100%" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack>
              <Typography variant="h6">{line.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {line.businessDate ? formatDayTitle(line.businessDate) : ""}
              </Typography>
            </Stack>
            <IconButton aria-label="ปิด" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack gap={0.5} sx={{ p: 2, borderRadius: 2, bgcolor: "background.default" }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2">กำไร (ขาดทุน)</Typography>
              <Chip size="small" label={isEstimate ? ESTIMATE_BADGE_TEXT : ACTUAL_BADGE_TEXT} />
              <Typography variant="body2" sx={{ ml: "auto", fontVariantNumeric: "tabular-nums" }}>
                {percentText(figures.profit_percent, WHOLE_PERCENT_DECIMALS)}
              </Typography>
            </Stack>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                fontVariantNumeric: "tabular-nums",
                color: profitTone ? TONE_COLOR[profitTone] : "text.primary",
                ...(isNegativeMoney(figures.profit) && { color: "error.main" }),
              }}
            >
              {moneyText(figures.profit)}
            </Typography>
          </Stack>
          <Section title="ยอดขาย" total={moneyText(figures.sales)}>
            <DetailRow label="เป้าหมาย" value={moneyText(figures.target)} />
            <DetailRow label="ยอดขาย" value={moneyText(figures.sales)} />
            <DetailRow
              label="ผล"
              value={figures.result === null ? "" : moneyText(figures.result)}
              isNegative={isNegativeMoney(figures.result)}
            />
          </Section>
          <Divider />
          <Section title="ต้นทุน" total={moneyText(figures.total_cost)}>
            <DetailRow label="ขาย" value={moneyText(figures.cost)} />
            <DetailRow label="เสีย" value={moneyText(figures.waste_cost)} />
            <DetailRow label="% เสีย" value={percentText(figures.waste_percent, PERCENT_DECIMALS)} />
            <DetailRow
              label="กำไรขั้นต้น"
              value={`${moneyText(figures.gross_profit)} · ${percentText(figures.gross_profit_percent, WHOLE_PERCENT_DECIMALS)}`}
              isNegative={isNegativePercent(figures.gross_profit_percent, WHOLE_PERCENT_DECIMALS)}
            />
          </Section>
          <Divider />
          <Section title="ค่าใช้จ่าย" total={moneyText(figures.expense_total)}>
            <DetailRow
              label="ฝ่ายขาย"
              value={moneyText(figures.sales_expense)}
              isEstimate={isEstimate}
              badge={expenseBadge}
            />
            <DetailRow
              label="บริหาร"
              value={moneyText(figures.manager_expense)}
              isEstimate={isEstimate}
              badge={expenseBadge}
            />
          </Section>
          <Divider />
          <Section title="ค่าน้ำมัน (รวมอยู่ในฝ่ายขาย ไม่หักซ้ำ)" total={moneyText(figures.fuel_amount)}>
            <DetailRow label="น้ำมันเดบิต (เงินสด)" value={moneyText(figures.fuel_debit)} />
            <DetailRow label="น้ำมันเครดิต (บัตร)" value={moneyText(figures.fuel_credit)} />
            <DetailRow label="% ของยอดขาย" value={percentText(figures.fuel_percent, PERCENT_DECIMALS)} />
            <DetailRow label="บิลเฉลี่ย" value={countText(figures.bills_per_route_day)} />
          </Section>
          <Stack direction="row" gap={1} sx={{ mt: "auto" }}>
            <Button variant="outlined" disabled={!onPrevious} onClick={onPrevious ?? undefined}>
              ‹ วันก่อน
            </Button>
            <Button variant="outlined" disabled={!onNext} onClick={onNext ?? undefined}>
              วันถัดไป ›
            </Button>
            <Button variant="contained" onClick={onClose} sx={{ ml: "auto" }}>
              ปิด
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
